import type { Region } from './types';

export class RegionDetector {
  private static readonly EU_COUNTRIES = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'IS',
    'LI',
    'NO',
  ];

  private cachedRegion: Region | null = null;

  public async detectRegion(): Promise<Region> {
    if (this.cachedRegion) {
      return this.cachedRegion;
    }

    let region: Region = 'unknown';

    try {
      region =
        this.detectFromTimezone() ||
        this.detectFromLanguage() ||
        (await this.detectFromIP()) ||
        'unknown';
    } catch (error) {
      console.warn(
        'ConsentManager: Region detection failed, using unknown',
        error
      );
    }

    this.cachedRegion = region;
    return region;
  }

  private detectFromTimezone(): Region | null {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (timezone.includes('Europe/')) {
        const country = this.extractCountryFromTimezone(timezone);
        if (country === 'GB') return 'UK';
        if (RegionDetector.EU_COUNTRIES.includes(country)) return 'EU';
      }

      if (timezone.includes('America/')) {
        if (
          timezone.includes('Toronto') ||
          timezone.includes('Vancouver') ||
          timezone.includes('Montreal') ||
          timezone.includes('Halifax')
        ) {
          return 'CA';
        }
        return 'US';
      }

      if (timezone.includes('Australia/')) return 'AU';
      if (timezone.includes('Asia/Kolkata') || timezone.includes('Asia/Mumbai'))
        return 'IN';
      if (
        timezone.includes('America/Sao_Paulo') ||
        timezone.includes('America/Brasilia')
      )
        return 'BR';
    } catch (error) {
      console.warn('ConsentManager: Timezone detection failed', error);
    }

    return null;
  }

  private detectFromLanguage(): Region | null {
    try {
      const languages = navigator.languages || [navigator.language];
      const primaryLanguage = languages[0]?.toLowerCase();

      if (primaryLanguage?.includes('en-gb')) return 'UK';
      if (primaryLanguage?.includes('en-us')) return 'US';
      if (
        primaryLanguage?.includes('en-ca') ||
        primaryLanguage?.includes('fr-ca')
      )
        return 'CA';
      if (primaryLanguage?.includes('en-au')) return 'AU';
      if (primaryLanguage?.includes('pt-br')) return 'BR';
      if (primaryLanguage?.includes('hi') || primaryLanguage?.includes('en-in'))
        return 'IN';

      const euLanguages = [
        'de',
        'fr',
        'es',
        'it',
        'nl',
        'pl',
        'pt',
        'sv',
        'da',
        'fi',
        'no',
      ];
      if (euLanguages.some((lang) => primaryLanguage?.startsWith(lang))) {
        return 'EU';
      }
    } catch (error) {
      console.warn('ConsentManager: Language detection failed', error);
    }

    return null;
  }

  private async detectFromIP(): Promise<Region | null> {
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        signal: AbortSignal.timeout(3000),
      });

      if (!response.ok) return null;

      const data = await response.json();
      const countryCode = data.country_code?.toUpperCase();

      if (!countryCode) return null;

      if (countryCode === 'GB') return 'UK';
      if (countryCode === 'US') return 'US';
      if (countryCode === 'CA') return 'CA';
      if (countryCode === 'AU') return 'AU';
      if (countryCode === 'BR') return 'BR';
      if (countryCode === 'IN') return 'IN';
      if (RegionDetector.EU_COUNTRIES.includes(countryCode)) return 'EU';
    } catch (error) {
      console.warn('ConsentManager: IP-based region detection failed', error);
    }

    return null;
  }

  private extractCountryFromTimezone(timezone: string): string {
    const parts = timezone.split('/');
    const city = parts[parts.length - 1];

    const cityToCountry: Record<string, string> = {
      London: 'GB',
      Dublin: 'IE',
      Paris: 'FR',
      Berlin: 'DE',
      Rome: 'IT',
      Madrid: 'ES',
      Amsterdam: 'NL',
      Brussels: 'BE',
      Vienna: 'AT',
      Warsaw: 'PL',
      Prague: 'CZ',
      Budapest: 'HU',
      Stockholm: 'SE',
      Copenhagen: 'DK',
      Helsinki: 'FI',
      Oslo: 'NO',
      Zurich: 'CH',
      Athens: 'GR',
      Lisbon: 'PT',
    };

    return cityToCountry[city] || 'EU';
  }

  public clearCache(): void {
    this.cachedRegion = null;
  }

  public isEU(region: Region): boolean {
    return region === 'EU' || region === 'UK';
  }

  public requiresOptIn(region: Region): boolean {
    return this.isEU(region) || region === 'CA' || region === 'BR';
  }

  public requiresDoNotSell(region: Region): boolean {
    return region === 'US' || region === 'CA';
  }
}
